import { useState, useEffect } from 'react';
import { getAllUsers, getICMembersByFund, getAllFunds, saveICMember } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import Button from '../../shared/UI/Button';

export default function ICMemberForm({ member, fundId, onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(member
    ? { user_id: member.user_id, is_chair: member.is_chair || false, appointed_at: member.appointed_at?.toDate?.()?.toISOString?.()?.split('T')[0] || new Date().toISOString().split('T')[0] }
    : { user_id: '', is_chair: false, appointed_at: new Date().toISOString().split('T')[0] }
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [icUsers, setIcUsers] = useState([]);
  const [existingChair, setExistingChair] = useState(null);
  const [showChairWarning, setShowChairWarning] = useState(false);
  const [funds, setFunds] = useState([]);

  useEffect(() => {
    Promise.all([
      getAllUsers(),
      getICMembersByFund(fundId),
      getAllFunds(),
    ]).then(([users, members, allFunds]) => {
      setIcUsers(users.filter(u => (u.roles || [u.role]).includes('ic_member') && u.is_active !== false));
      const chair = members.find(m => m.is_chair && m.is_active !== false && m.user_id !== member?.user_id);
      setExistingChair(chair || null);
      setFunds(allFunds);
    });
  }, [fundId, member]);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function handleChairToggle(value) {
    if (value && existingChair) {
      setShowChairWarning(true);
    } else {
      set('is_chair', value);
    }
  }

  function confirmChair() {
    set('is_chair', true);
    setShowChairWarning(false);
  }

  function validate() {
    const e = {};
    if (!form.user_id) e.user_id = 'Please select a user';
    if (!form.appointed_at) e.appointed_at = 'Appointed date is required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const selectedUser = icUsers.find(u => u.id === form.user_id);
      const appointedDate = new Date(form.appointed_at);
      await saveICMember({
        fundId,
        userId: form.user_id,
        memberData: {
          full_name: selectedUser?.full_name || '',
          email: selectedUser?.email || '',
          is_chair: form.is_chair,
          appointed_at: appointedDate,
          is_active: true,
        },
        performedBy: user.uid,
      });
      setSuccessMsg('IC member saved successfully!');
      setTimeout(() => { setSuccessMsg(''); onSaved(); }, 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  const fundName = funds.find(f => f.id === fundId)?.name || fundId;
  const userOptions = icUsers.map(u => ({ value: u.id, label: `${u.full_name} (${u.email})` }));

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {showChairWarning && (
        <div className="alert alert-warning mb-4">
          <div>
            <strong>Replace existing chair?</strong>
            <p style={{ marginTop: 4, fontSize: 13 }}>
              {existingChair?.full_name} is currently the IC chair for this fund. Setting this member as chair will remove that designation from {existingChair?.full_name}.
            </p>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <Button type="button" size="sm" variant="primary" onClick={confirmChair}>Confirm</Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => setShowChairWarning(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      <div className="form-grid mb-4">
        <FormField label="Fund" hint="Read-only">
          <input className="form-input" value={fundName} readOnly
            style={{ background: 'var(--color-border-light)', cursor: 'not-allowed' }} />
        </FormField>
        <SelectField
          label="IC Member (User)"
          required
          error={errors.user_id}
          value={form.user_id}
          onChange={e => set('user_id', e.target.value)}
          options={userOptions}
          placeholder="Select IC member…"
          disabled={!!member}
        />
        <ToggleField
          label="Is Chair"
          checked={form.is_chair}
          onChange={handleChairToggle}
          hint="Only one member per fund can be chair"
        />
        <FormField label="Appointed Date" required error={errors.appointed_at}>
          <input
            type="date"
            className={`form-input ${errors.appointed_at ? 'error' : ''}`}
            value={form.appointed_at}
            onChange={e => set('appointed_at', e.target.value)}
          />
        </FormField>
      </div>

      {userOptions.length === 0 && (
        <div className="alert alert-warning mb-4">
          No users with the "IC Member" role exist yet. Create IC member user accounts in the Users section first.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {member ? 'Save Changes' : 'Add IC Member'}
        </Button>
      </div>
    </form>
  );
}
